import React, { useState, useEffect, useMemo } from "react";
import { fetchChatHistory } from "../api/chat.api";
import { MessageType } from "@/types";
import { useErrorStore } from "../store/error.store";
import { DEFAULT_WELCOME_MESSAGE } from "../constants/const";
import { getChatbotConfig } from "../utils/utils";

export function useChatHistory(
  sessionKey: string | null,
  setAllMessages: React.Dispatch<React.SetStateAction<MessageType[]>>
) {
  const config = useMemo(() => getChatbotConfig(), []);
  const { corporate_id: tenantId, corporate_uuid: tenantUuId } = config;
  const setError = useErrorStore((state) => state.setError);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);

  /**
   * チャット履歴を読み込む
   * @param pageNum ページ番号
   */
  const loadHistory = async (pageNum: number = 0) => {
    setIsLoadingMore(pageNum > 0);

    try {
      let currentLastMessageId: string | undefined;
      if (pageNum === 0) {
        currentLastMessageId = undefined;
      } else {
        currentLastMessageId = lastMessageId || undefined;
      }
      // チャット履歴を取得する
      const result = await fetchChatHistory(pageNum, currentLastMessageId);

      if (result && result.messages && result.messages.length > 0) {
        if (pageNum === 0) {
          setAllMessages(result.messages);
          if (result.messages.length > 0) {
            setLastMessageId(result.messages[0].id);
          }
        } else {
          setAllMessages((prev) => {
            const existingIds = new Set(prev.map((msg) => msg.id));
            const newMessages = result.messages.filter(
              (msg) => !existingIds.has(msg.id)
            );
            return [...newMessages, ...prev];
          });
          if (result.messages.length > 0) {
            setLastMessageId(result.messages[0].id);
          }
        }
        setPage(pageNum);
        setHasMore(result.hasMore);
      } else {
        setHasMore(false);
        if (pageNum === 0) {
          setAllMessages([DEFAULT_WELCOME_MESSAGE]);
        }
      }
    } catch (error) {
      console.error("チャット履歴取得に失敗しました:", error);
      if (pageNum === 0) {
        setAllMessages([DEFAULT_WELCOME_MESSAGE]);
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError("チャット履歴取得に失敗しました");
        }
      }
    } finally {
      setIsLoadingMore(false);
    }
  };

  /**
   * 次のページ
   */
  const loadMore = () => {
    if (!isLoadingMore && hasMore && lastMessageId) {
      loadHistory(page + 1);
    }
  };

  // 有効なセッションがある場合のみ
  useEffect(
    () => {
      if (sessionKey) {
        loadHistory(0);
      } else {
        // セッションがない場合は初期化を待つ
        setAllMessages([]);
        setHasMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionKey, tenantId, tenantUuId]
  );

  // 最初の挨拶文もを取得する（セッションリセット時に使用）
  const resetHistory = async () => {
    loadHistory(0);
  };

  return {
    isLoadingMore,
    hasMore,
    loadMore,
    resetHistory,
  };
}
